import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import {WithdrawSeoulAgreementRequest} from '@di-shared/api/di/__generated';
import {emitEventLog} from '@di-shared/features/analytics';
import useApiError from '@di-shared/features/common/useApiError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import MileageBalanceTooltip from '@di-shared/features/mileage/ecomileage/MileageBalanceTooltip';
import useMileageBalance from '@di-shared/features/mileage/ecomileage/useMileageBalance';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

const ECOMILEAGE_GOODS_INTRO_URL = 'https://ecomileage.seoul.go.kr/goods/apply.do';

interface MileageBalanceProps {
  vin: string;
}

function MileageBalance({vin}: MileageBalanceProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {resolveError} = useApiError();

  const [withdrawMileage] = usePendingHandler(
    async () => {
      await diApi.ecoMileageController.withdraw({
        vin,
        requestBody: {
          vin,
          stopCd: WithdrawSeoulAgreementRequest.stopCd._02,
        },
      });
    },
    async (error) => {
      await resolveError(error);
    }
  );

  return (
    <section className="section">
      <h2 className="blind">{t('T30177')}</h2>
      <section className="section section_mileage_history">
        <div className="section_heading type_main">
          <h3
            className="section_heading_title"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30178')}
          </h3>
        </div>
        <ErrorBoundary fallback={<MileageBalanceDisplayError />}>
          <Suspense fallback={<MileageBalanceDisplayLoading />}>
            <MileageBalanceDisplay vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <p className="notice_refer">{t('T30176')}</p>

        <button
          type="button"
          className="btn btn_large btn_outline btn_full"
          onClick={() => {
            emitEventLog({
              event: 'mileage_management',
              interaction_type: 'use mileage',
              click_text: t('T30175'),
              click_location: 'body',
            });
            WebviewUtils.openWebBrowser(ECOMILEAGE_GOODS_INTRO_URL);
          }}
        >
          {t('T30175')}
        </button>
        <button
          type="button"
          className="contract_withdraw_btn"
          onClick={async () => {
            const selectedIndex = await WebviewUtils.openCustomAlert({
              title: t('T30353'),
              message: t('T30354'),
              buttons: [
                {text: t('T20119'), style: 'PRIMARY', actionType: 'CANCEL'},
                {text: t('T30355'), style: 'BASIC', actionType: 'DEFAULT'},
              ],
            });

            if (selectedIndex === 1) {
              emitEventLog({
                event: 'mileage_management',
                interaction_type: 'unlink mileage',
                click_text: t('T30184'),
                click_location: 'body',
              });
              await withdrawMileage();
              navigate(ROUTE_PATHS.HOME.getFullPath(vin) + '?tab=benefit', {replace: true});
            }
          }}
        >
          {t('T30184')}
        </button>
      </section>
    </section>
  );
}

function MileageBalanceDisplay({vin}: MileageBalanceProps) {
  const {t} = useInferableTranslation();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {totalRemainPoint, totalAccumulatedPoint, latestMileageHistory} = useMileageBalance(vin);
  return (
    <>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="body_large_b">{t('T30179')}</h4>
          <MileageBalanceTooltip />
          <span className="eco_mileage_amount">{formatNumberWithCommas(totalRemainPoint)}</span>
        </div>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4
            className="body_large_b"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30180')}
          </h4>
          <span className="eco_mileage_amount">{formatNumberWithCommas(totalAccumulatedPoint)}</span>
        </div>
        {!!totalAccumulatedPoint && (
          <>
            <div className="divider" />
            <div className="mileage_history">
              <table className="mileage_history_table">
                <caption>
                  <span className="blind">{t('T30181')}</span>
                </caption>
                <colgroup>
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">{t('T30182')}</th>
                    <th scope="col">{t('T30183')}</th>
                  </tr>
                </thead>
                <tbody>
                  {latestMileageHistory.map((history, index) => (
                    <tr key={history.pynmt_incntv_de + history.pynmt_incntv.toString()}>
                      <th scope="row">{history.pynmt_incntv_de && DashedDatetimeFormatter.format(history.pynmt_incntv_de, 'date')}</th>
                      <td>+ {formatNumberWithCommas(history.pynmt_incntv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default MileageBalance;

export function MileageBalanceDisplayError() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_container">
        <div className="section_container_header">
          <h4 className="body_large_b">{t('T30179')}</h4>
          <MileageBalanceTooltip />
          <span className="eco_mileage_amount">-</span>
        </div>
      </div>
      <div className="section_container">
        <div className="section_container_header">
          <h4
            className="body_large_b"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30180')}
          </h4>
          <span className="eco_mileage_amount">-</span>
        </div>
      </div>
    </>
  );
}

export function MileageBalanceDisplayLoading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_container skeleton">
        <div className="section_container_header">
          <h4 className="body_large_b">{t('T30179')}</h4>
          <MileageBalanceTooltip />
          <span className="eco_mileage_amount">-</span>
        </div>
      </div>
      <div className="section_container">
        <div className="section_container_header skeleton">
          <h4
            className="body_large_b"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T30180')}
          </h4>
          <span className="eco_mileage_amount">-</span>
        </div>
        <div className="divider"></div>

        <div className="mileage_history">
          <table className="mileage_history_table skeleton">
            <caption>
              <span className="blind">{t('T30181')}</span>
            </caption>
            <colgroup>
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{t('T30182')}</th>
                <th scope="col">{t('T30183')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row"></th>
                <td>+</td>
              </tr>
              <tr>
                <th scope="row"></th>
                <td>+</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
